import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { RoutesBuilderService, SITE_USER_MODULES, SITE_USER_SETTINGS_MODULES } from '@app/config';
import { EUserPermission } from '@project/shared';
import { TranslateService } from '@project/translate';
import { EFeatureFlags, ESidebarIconType, ISiteModule } from '@app/config';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IProfileFull, EProfileTypeDTO, IDoctorHealthCenterDTO } from '@project/view-models';
import { HcService, UserProfileDataService } from '@project/services';
import { AuthorisationStateService } from '@project/services';
import { DoctorsApiProviderService } from '@project/data-providers';
import { commonEnvironment } from '../../../../../environments/common';
import { LocalStorageService } from 'src/app/project/services/local-storage.service';
import { HCConfigRules } from '../../../../../hc-config/hc-config';
import { monitorLogger } from '../../../monitor/monitor-logger';

interface IMenuItem {
  title: string;
  routeLink: string;
  userPermissions: EUserPermission[];
  icon?: ESidebarIconType;
  featureFlag?: EFeatureFlags;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, OnDestroy {
  public readonly RoutesBuilderService = RoutesBuilderService;
  private _healthCenters$ = new BehaviorSubject<IDoctorHealthCenterDTO[]>([]);
  public healthCenters$ = this._healthCenters$.asObservable();
  public selectedHC: string;
  public disabledSwitch = false;
  private listener: any;
  public version: string = commonEnvironment.appVersion;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private userProfileDataService: UserProfileDataService,
    private authorizationStateService: AuthorisationStateService,
    private doctorsApiProviderService: DoctorsApiProviderService,
    private localStorageService: LocalStorageService,
    private hcService: HcService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.userProfileDataService.isDoctor().subscribe((isDoctor) => {
      if (isDoctor) {
        this.doctorsApiProviderService
          .getAllHealthCenters(this.userProfileDataService.profile.id)
          .subscribe((healthCenters) => {
            this._healthCenters$.next(healthCenters);
          });

        this.selectedHC = this.authorizationStateService.getAuthorizationData().healthCenterId;

        this.listener = window.addEventListener('specialist-busy', (event) => {
          this.disabledSwitch = event['detail'];
        });
      }
    });

    // Remote HC config can change in realtime (module feature/rule gating). This component is
    // OnPush, so without this the menu would only pick up the new value after a manual reload.
    // Rebuilds the arrays here (rare event) rather than via a getter read on every CD cycle.
    this.hcService.configUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.navigationMenu = this.buildNavigationMenu();
      this.settingsMenu = this.buildSettingsMenu();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this.listener) {
      window.removeEventListener('specialist-busy', this.listener);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  public userData$: Observable<IProfileFull | null> = this.userProfileDataService.profile$;

  public userType: string = JSON.parse(this.localStorageService.getItem('auth')).userType;
  public isPatient: boolean = this.userType === EProfileTypeDTO.Patient;

  @Output() respToggled = new EventEmitter();

  // Built once and only rebuilt when HcService.configUpdated$ fires (see ngOnInit) - NOT a
  // getter. This array feeds a plain *ngFor: a getter would allocate a brand-new array/objects
  // on every change-detection run (this component checks often - polling/websocket activity
  // elsewhere in the app triggers frequent ticks), and without a trackBy that makes NgForOf
  // destroy and rebuild the whole menu's DOM every single tick instead of once.
  public navigationMenu: IMenuItem[] = this.buildNavigationMenu();
  public settingsMenu: IMenuItem[] = this.buildSettingsMenu();

  public readonly logoutMenuItem: IMenuItem = {
    title: TranslateService.localize('actions.logout'),
    routeLink: RoutesBuilderService.LOGOUT.logout(),
    userPermissions: [],
    icon: ESidebarIconType.Logout,
  };

  hideMenu(): void {
    this.respToggled.emit(false);
  }

  trackMenuClick(title: string): void {
    const eventMap: { [key: string]: string } = {
      [TranslateService.localize('sidebar.menu-labels.sessions')]: 'clicar_sessoes',
      [TranslateService.localize('sidebar.menu-labels.my-observations')]: 'clicar_dados_de_saude',
      [TranslateService.localize('sidebar.menu-labels.observations')]: 'clicar_dados_de_saude',
      [TranslateService.localize('sidebar.menu-labels.encounter-history')]: 'clicar_historio_de_consultas',
      [TranslateService.localize('sidebar.menu-labels.patient-exams')]: 'clicar_exames_do_paciente',
      [TranslateService.localize('sidebar.menu-labels.main-settings')]: 'clicar_configuracoes_principais',
    };

    const eventName = eventMap[title];
    if (eventName) {
      monitorLogger.screen.trackScreen('sidebar', eventName);
    }
  }

  private buildNavigationMenu(): IMenuItem[] {
    return [
      {
        title: TranslateService.localize('sidebar.menu-labels.sessions'),
        routeLink: RoutesBuilderService.SESSIONS.root(),
        userPermissions: [EUserPermission.havePatientSession],
        icon: ESidebarIconType.Document,
      },
      {
        title: TranslateService.localize('sidebar.menu-labels.sessions'),
        routeLink: RoutesBuilderService.SESSIONS.root(),
        userPermissions: [EUserPermission.haveDoctorSession],
        icon: ESidebarIconType.Document,
      },
      ...this.mapSiteModulesToSidebarItems(SITE_USER_MODULES.filter((module) => this.canShowModule(module))),
    ];
  }

  private buildSettingsMenu(): IMenuItem[] {
    return [...this.mapSiteModulesToSidebarItems(SITE_USER_SETTINGS_MODULES)];
  }

  private mapSiteModulesToSidebarItems(modules: ISiteModule[]): IMenuItem[] {
    return modules.map((module) => {
      return {
        title: module.sidebarLabelCreator(),
        routeLink: module.rootFullRoute,
        userPermissions: module.permissions ?? [],
        icon: module.sidebarIcon,
      };
    });
  }

  public onUserCardClick(): void {
    this.router.navigateByUrl(this.router.url + `(globalAside:information-center/${this.userType}`);
  }

  private canShowModule(module: ISiteModule): boolean {
    return this.hasRequiredFeatures(module) && this.hasRequiredRules(module);
  }

  private hasRequiredFeatures(module: ISiteModule): boolean {
    if (!module.hasHcFeature?.length) return true;

    return module.hasHcFeature.every((feature) => this.hcService.hasFeature(feature));
  }

  private hasRequiredRules(module: ISiteModule): boolean {
    if (!module.hasHcRule?.length) return true;

    return module.hasHcRule.every((rule) => this.hcService.hasRule(rule));
  }
}
